import { useEffect, useMemo, useState } from "react";
import { Plus, Upload, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, Money, Modal, Field, ErrBox, Table, Empty } from "../../components/common";

const EMPTY_TX = { date: "", processor: "Manual", gross: "", taxable: "", tax_collected: "", refund: "0",
  tip: "0", discount: "0", fee: "0", category: "Retail Sales", location: "", jurisdiction: "", notes: "" };
const MAP_FIELDS = [["", "— Skip —"], ["date", "Date"], ["tx_id", "Transaction ID"], ["processor", "Merchant Processor"],
  ["gross", "Gross Amount"], ["refund", "Refund"], ["tax_collected", "Tax Collected"], ["tip", "Tip"],
  ["discount", "Discount"], ["fee", "Fee"], ["net", "Net Deposit"], ["taxable", "Taxable Amount"],
  ["category", "Category"], ["location", "Location"], ["jurisdiction", "Tax Jurisdiction"], ["notes", "Notes"]];

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return null;
  const split = (l) => {
    const out = []; let cur = ""; let q = false;
    for (const ch of l) {
      if (ch === '"') q = !q;
      else if (ch === "," && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  return { headers: split(lines[0]), rows: lines.slice(1).map(split) };
}

export default function Sales() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [processor, setProcessor] = useState("");
  const [sort, setSort] = useState("-date");
  const [selected, setSelected] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TX);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [bulkCategory, setBulkCategory] = useState("");

  const load = () => {
    const q = new URLSearchParams({ page, page_size: 25, search, processor, sort });
    API.get(`/transactions?${q}`).then((r) => { setData(r.data); setSelected([]); });
  };
  useEffect(() => { load(); }, [page, processor, sort]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]);

  const processors = useMemo(() => ["Square POS", "Stripe", "Toast", "Clover", "Shopify", "Manual", "Cash", "Check", "ACH"], []);

  const submitAdd = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.post("/transactions", form);
      toast.success("Transaction added");
      setAddOpen(false); setForm(EMPTY_TX); load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const onCsvFile = (file) => {
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(reader.result);
      if (!parsed) { setErr("Could not parse CSV — need a header row plus at least one data row."); return; }
      setCsv(parsed);
      const auto = {};
      parsed.headers.forEach((h) => {
        const hl = h.toLowerCase();
        const match = MAP_FIELDS.find(([v]) => v && (hl.includes(v.replace(/_/g, " ")) || hl.includes(v)));
        auto[h] = match ? match[0] : (hl.includes("gross") ? "gross" : hl.includes("date") ? "date" : "");
      });
      setMapping(auto);
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    setBusy(true); setErr("");
    const rows = csv.rows.map((r) => {
      const obj = {};
      csv.headers.forEach((h, i) => { if (mapping[h]) obj[mapping[h]] = r[i]; });
      return obj;
    }).filter((r) => r.date || r.gross);
    try {
      const { data: res } = await API.post("/transactions/import", { rows });
      setImportResult(res);
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const bulkApply = async () => {
    if (!bulkCategory || selected.length === 0) return;
    try {
      const { data: res } = await API.post("/transactions/bulk", { ids: selected, updates: { category: bulkCategory } });
      toast.success(`Updated ${res.updated} transactions`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSort = (col) => setSort(sort === `-${col}` ? col : `-${col}`);

  return (
    <div data-testid="sales-page">
      <PageHeader title="Sales & Transactions" sub="Every dollar in, categorized and reconciled."
        right={<>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/export/csv`} className="btn-outline-gov" data-testid="export-csv-btn"><Download className="h-4 w-4" /> Export</a>
          <button className="btn-outline-gov" onClick={() => { setImportOpen(true); setCsv(null); setImportResult(null); setErr(""); }} data-testid="import-csv-btn"><Upload className="h-4 w-4" /> Import CSV</button>
          <button className="btn-navy" onClick={() => setAddOpen(true)} data-testid="add-transaction-btn"><Plus className="h-4 w-4" /> Add Sale</button>
        </>} />

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="input-gov max-w-xs" placeholder="Search ID, notes, location…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tx-search" />
        <select className="input-gov max-w-[180px]" value={processor} onChange={(e) => { setProcessor(e.target.value); setPage(1); }} data-testid="tx-processor-filter">
          <option value="">All processors</option>
          {processors.map((p) => <option key={p}>{p}</option>)}
        </select>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-gold/50 bg-gold-50 px-3 py-1.5" data-testid="bulk-bar">
            <span className="text-sm font-semibold text-navy">{selected.length} selected</span>
            <select className="rounded border border-slate-300 px-2 py-1 text-sm" value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} data-testid="bulk-category">
              <option value="">Set category…</option>
              {["Retail Sales", "Restaurant", "Services", "Wholesale", "Exempt"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <button className="btn-navy px-3 py-1 text-xs" onClick={bulkApply} data-testid="bulk-apply">Apply</button>
          </div>
        )}
      </div>

      {!data ? <Loading label="Loading transactions" /> : data.items.length === 0 ? (
        <Empty title="No transactions yet" msg="Add sales manually, import a CSV, or connect a merchant processor." />
      ) : (
        <>
          <Table testid="transactions-table" headers={["", <button onClick={() => toggleSort("date")} className="uppercase">Date {sort.includes("date") && (sort[0] === "-" ? "↓" : "↑")}</button>,
            "Transaction", "Processor", <button onClick={() => toggleSort("gross_cents")} className="uppercase">Gross</button>, "Taxable", "Non-Taxable", "Tax Collected", "Category", "Location"]}>
            {data.items.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t.tx_id}`}>
                <td className="px-3 py-2.5"><input type="checkbox" checked={selected.includes(t.id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, t.id] : selected.filter((x) => x !== t.id))} /></td>
                <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(t.date)}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{t.tx_id}</td>
                <td className="px-4 py-2.5">{t.processor}</td>
                <td className="px-4 py-2.5 money font-semibold text-navy"><Money cents={t.gross_cents} exact /></td>
                <td className="px-4 py-2.5 money text-slate-700"><Money cents={t.taxable_cents} exact /></td>
                <td className="px-4 py-2.5 money text-slate-500"><Money cents={t.non_taxable_cents} exact /></td>
                <td className="px-4 py-2.5 money text-slate-700"><Money cents={t.tax_collected_cents} exact /></td>
                <td className="px-4 py-2.5"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{t.category}</span></td>
                <td className="px-4 py-2.5 text-slate-500">{t.location || "—"}</td>
              </tr>
            ))}
          </Table>
          <div className="mt-4 flex items-center justify-between" data-testid="tx-pagination">
            <span className="text-sm text-slate-500">{data.total} transactions · page {data.page} of {data.pages || 1}</span>
            <div className="flex gap-2">
              <button className="btn-outline-gov px-3 py-1.5" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tx-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button className="btn-outline-gov px-3 py-1.5" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="tx-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Manual Sale" wide>
        <form onSubmit={submitAdd} className="grid gap-4 sm:grid-cols-2" data-testid="add-tx-form">
          <Field label="Date *"><input type="date" className="input-gov" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="tx-date" /></Field>
          <Field label="Source">
            <select className="input-gov" value={form.processor} onChange={(e) => setForm({ ...form, processor: e.target.value })} data-testid="tx-processor">
              {processors.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Gross Amount ($) *"><input className="input-gov money" inputMode="decimal" value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} required data-testid="tx-gross" /></Field>
          <Field label="Taxable Amount ($)" hint="Defaults to gross"><input className="input-gov money" inputMode="decimal" value={form.taxable} onChange={(e) => setForm({ ...form, taxable: e.target.value })} data-testid="tx-taxable" /></Field>
          <Field label="Tax Collected ($)"><input className="input-gov money" inputMode="decimal" value={form.tax_collected} onChange={(e) => setForm({ ...form, tax_collected: e.target.value })} data-testid="tx-tax-collected" /></Field>
          <Field label="Refunds ($)"><input className="input-gov money" inputMode="decimal" value={form.refund} onChange={(e) => setForm({ ...form, refund: e.target.value })} data-testid="tx-refund" /></Field>
          <Field label="Tips ($)"><input className="input-gov money" inputMode="decimal" value={form.tip} onChange={(e) => setForm({ ...form, tip: e.target.value })} data-testid="tx-tip" /></Field>
          <Field label="Processor Fees ($)"><input className="input-gov money" inputMode="decimal" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="tx-fee" /></Field>
          <Field label="Category">
            <select className="input-gov" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tx-category">
              {["Retail Sales", "Restaurant", "Services", "Wholesale", "Exempt"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Location"><input className="input-gov" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="tx-location" /></Field>
          <Field label="Tax Jurisdiction"><input className="input-gov" placeholder="Phoenix, AZ" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} data-testid="tx-jurisdiction" /></Field>
          <Field label="Notes"><input className="input-gov" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tx-notes" /></Field>
          <div className="sm:col-span-2"><ErrBox msg={err} /></div>
          <button className="btn-navy sm:col-span-2" disabled={busy} data-testid="tx-submit">{busy ? "Saving…" : "Save Transaction"}</button>
        </form>
      </Modal>

      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Import Sales from CSV" wide>
        <div className="space-y-4" data-testid="csv-import">
          {!csv && !importResult && (
            <div className="rounded-md border-2 border-dashed border-slate-300 p-8 text-center">
              <Upload className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-2 text-sm text-slate-600">Choose a CSV with a header row. You'll map columns before anything is imported.</p>
              <input type="file" accept=".csv" onChange={(e) => e.target.files[0] && onCsvFile(e.target.files[0])} className="mt-4 text-sm" data-testid="csv-file-input" />
            </div>
          )}
          {csv && !importResult && (
            <>
              <div className="label-gov">Map CSV columns to transaction fields</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {csv.headers.map((h) => (
                  <div key={h} className="flex items-center gap-2 text-sm">
                    <span className="w-32 truncate font-mono text-xs text-slate-500" title={h}>{h}</span>
                    <span className="text-slate-400">→</span>
                    <select className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm" value={mapping[h] || ""}
                      onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`map-${h}`}>
                      {MAP_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="label-gov">Preview (first 3 of {csv.rows.length} rows)</div>
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50">{csv.headers.map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
                  <tbody>{csv.rows.slice(0, 3).map((r, i) => <tr key={i} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className="px-2 py-1.5">{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <ErrBox msg={err} />
              <button className="btn-copper w-full" onClick={runImport} disabled={busy} data-testid="csv-import-run">
                {busy ? "Importing…" : `Import ${csv.rows.length} Rows`}
              </button>
            </>
          )}
          {importResult && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="import-result">
              <div className="text-lg font-bold text-emerald-800">{importResult.imported} transactions imported</div>
              {importResult.errors.length > 0 && (
                <div className="mt-2 text-left text-xs text-rose-700">
                  {importResult.errors.slice(0, 5).map((e2) => <div key={e2.row}>Row {e2.row}: {e2.error}</div>)}
                </div>
              )}
              <button className="btn-navy mt-4" onClick={() => setImportOpen(false)} data-testid="import-done">Done</button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
